import { type ReactNode, useCallback } from 'react';
import { ThemeProvider } from './ThemeProvider';
import { useThemeOverride, useSaveThemeOverride } from '@/api';
import { useAuth } from '@/auth';

interface ThemeProviderWithApiProps {
  children: ReactNode;
}

/**
 * Theme Provider with ABP Settings API Integration
 *
 * This wrapper:
 * 1. Loads theme override from motr.Settings.ThemeOverride on mount (when authenticated)
 * 2. Provides save functionality to persist theme to backend
 * 3. Handles loading states
 * 4. Falls back to base theme when not authenticated
 */
export const ThemeProviderWithApi = ({ children }: ThemeProviderWithApiProps) => {
  const { isAuthenticated } = useAuth();

  // Only fetch theme override when authenticated
  const { data: themeOverride, isLoading, error } = useThemeOverride(isAuthenticated);

  // Mutation to save theme
  const { mutateAsync: saveTheme } = useSaveThemeOverride();

  // Save handler
  const handleSaveTheme = useCallback(
    async (themeJson: string) => {
      if (!isAuthenticated) {
        console.warn('[Theme] Cannot save theme - not authenticated');
        throw new Error('Not authenticated');
      }
      try {
        await saveTheme(themeJson);
      } catch (error) {
        // Re-throw with more context
        const message = error instanceof Error ? error.message : 'Unknown error';
        console.error('[Theme] Failed to save theme:', error);
        throw new Error(`Failed to save theme: ${message}`);
      }
    },
    [saveTheme, isAuthenticated]
  );

  // Log any errors (but ignore 401 errors when not authenticated)
  if (error && isAuthenticated) {
    console.warn('[Theme] Failed to load theme from backend:', error);
  }

  return (
    <ThemeProvider
      tenantThemeOverride={isAuthenticated ? themeOverride : undefined}
      onSaveTheme={isAuthenticated ? handleSaveTheme : undefined}
      isLoading={isAuthenticated && isLoading}
    >
      {children}
    </ThemeProvider>
  );
};

export default ThemeProviderWithApi;
